import {useNotification} from "@/base/components/NotificationProvider";
import {useTranslation} from "next-i18next";
import {useUser} from "@/base/components/auth/UserProvider";
import {ConversationMessage} from "@/base/types/ConversationMessage";
import React, {forwardRef, useEffect, useRef, useState} from "react";
import PagedItems, {OnPageChange} from "@/base/components/dashboard/PagedItems";
import {getDefaultPageOutput, MapPageOutput, PageOutput} from "@/base/types/Pagination";
import {ModifyURL} from "@/base/utils/fetch";
import {Button, Image as AImage, Input, InputRef, Upload, UploadFile,} from "antd";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import SendOutlined from "@ant-design/icons/SendOutlined";
import UploadOutlined from "@ant-design/icons/UploadOutlined";
import {getRefValue, setRefValue} from "@/base/utils/form";
import {Loading} from "@/base/components/Loading";
import {usePusher} from "@/base/utils/pusher.provider";
import InteractiveLinks from "@/base/components/InteractiveLinks";
import ImgCrop from "antd-img-crop";
import ConversationStyles from '@/base/styles/Conversation.module.scss';
import {RcFile} from "antd/es/upload";
import {Media as Attachment} from "@/base/types/Media";
import {default as NextImage} from "next/image";
import {UserData} from "@/base/types/UserData";
import {DelayedEffectHook} from "@/base/types/delayed-effect.hook";
import {PublicUser} from "@/base/types/PublicUser";
import {MdDownload} from "react-icons/md";

export const Media = ({media, className = ''}: {
	media: Attachment,
	className?: string
}) => {
	const baseType = media.mime_type.split('/')[0];
	const name = media.basename || baseType;
	const ext = media.basename.split('.').pop();

	const isImage = baseType == 'image';

	const downloadFile = () => {
		if (media.media_url)
		{
			window.open(media.media_url, '_blank');
		}
	}

	return (isImage ?
			<div className={`rounded-md text-text-2  w-fit overflow-hidden ${className}`}>
				<AImage
					src={media.media_url}
					alt={name}
					className={`w-full rounded-md source`}
				/>
			</div> :
			<div className={`bg-gradient-to-bl from-primary-200 to-primary-100 rounded-md p-2 flex items-center hover:cursor-pointer hover:brightness-95 duration-150 ${className}`} onClick={downloadFile}>
				<MdDownload className={`text-primary me-2`} size={25}/>
				<p className={`text-primary`}>{name.split('.')[0]}.</p>
				<p className={`!text-primary !font-bold`}>{ext}</p>
			</div>
	)
}

const getColor = (seed: number) => {
	let color = Math.floor((Math.abs(Math.sin(seed) * 16777215))).toString(16);

	while (color.length < 6)
	{
		color = '0' + color;
	}

	return color;
}

const toSeed = (str: string) => {
	let hash = 0;
	if (str.length == 0) return hash;
	for (let i = 0; i < str.length; i++)
	{
		const char = str.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return hash;
}

export const isMeMessage = (message: ConversationMessage, user?: UserData | null) => {
	return user ? (message.user_id == user.id) : (message.user_id == -1 && message.role == 'user')
}

export const MessageAuthor = forwardRef(({message, className}: {
	message: (ConversationMessage & {
		color?: string
	}),
	className?: string
}, ref: React.ForwardedRef<HTMLDivElement>) => {
	return <div
		className={`flex gap-4 justify-center items-center ${className || ''}  w-fit mx-auto p-4 rounded-xl relative bg-base-bg`}
		ref={ref}
	>
		<div
			className={`absolute top-1/2 -translate-y-1/2 left-0 w-1/2 h-full rounded-l-xl bg-gradient-to-tl from-transparent from-50% opacity-20 pointer-events-none`}
			style={{
				'--tw-gradient-to': message.color ? `#${message.color}` : 'transparent',
			} as any}
		/>
		<NextImage src={message.user?.avatar_url || '/image/logo.svg'} unoptimized width={48} height={48} alt={message.user?.name || 'StableProxy'} className={`overflow-hidden rounded-xl aspect-square`}/>
		<div className={`flex flex-col gap-1`}>
			<p className={`text-md font-medium my-0`}>{message.user?.name || 'StableProxy user'}</p>
			<p className={`text-sm my-0`}>{message.user?.role || 'User'}</p>
		</div>
	</div>
});

// TODO: implement message reading

export const Message = forwardRef(({message, isMe, className}: {
	message: (ConversationMessage & {
		color?: string
	}),
	className?: string,
	isMe: boolean
}, ref: React.ForwardedRef<HTMLDivElement>) => {
	return <div
		id={`message-${message.uuid}`}
		className={`flex flex-col space-y-1 shadow-md ${message.message ? 'px-6 pt-4 pb-4' : 'px-2 pt-1 pb-2'} rounded-3xl mx-2
		relative  ${className || ''}
		conversation-message conversation-message-${isMe ? 'me' : 'other'}
			 ${isMe ?
			`self-end bg-primary !text-white lg:!ml-20 !rounded-br-none` :
			`self-start bg-main-bg lg:!mr-20 !rounded-bl-none`}
		`}
		ref={ref}
	>
		<div
			className={`absolute top-1/2 -translate-y-1/2 left-0 w-1/2 h-full rounded-l-3xl bg-gradient-to-tl from-transparent from-50% opacity-10 pointer-events-none`}
			style={{
				'--tw-gradient-to': isMe ? 'transparent' : (message.color ? `#${message.color}` : 'transparent'),
			} as any}
		>

		</div>
		<InteractiveLinks>
			{message.message && <p className={`whitespace-pre-wrap`}>
				{message.message}
			</p>}
			{message.media && message.media.length > 0 && <div className={`flex flex-col gap-2.5 items-end`}>
				{message.media.map((media, k) => <Media key={k} media={media} className={`!rounded-3xl overflow-hidden 
				${message.message ? `[&_.source]:h-[180px] [&_.source]:max-h-[180px] [&_.source]:min-h-[150px]` :
					`[&_.source]:h-[210px] [&_.source]:max-h-[210px] [&_.source]:min-h-[210px]`}
				 ${isMe ?
					`!rounded-br-none` :
					`!rounded-bl-none`}`}/>)}
			</div>}
		</InteractiveLinks>
		{/*{isLast && <Element name={'last-message'}/>}*/}
		<div className={`absolute ${!isMe ? `left-2 hidden` : `right-2`} bottom-1 text-[0.6rem]`}>
			<CheckOutlined className={`${!message.read_at && `opacity-30`}`}/>
			<CheckOutlined className={`-ml-[5px] ${!message.read_at && `opacity-40`}`}/>
		</div>
	</div>
});

type Props = {
	className?: string;
	initialText?: string;
	children?: React.ReactNode;
	beforeChildren?: React.ReactNode;
	lastMessageChildren?: React.ReactNode;
	firstMessageChildren?: React.ReactNode;
	conversationId: string | null;
	type: 'support' | 'chat';
	maxHeight?: number;
	canType?: boolean;
	onSend?: (form: FormData) => Promise<any>;
	loading?: boolean;
}

export default function Conversation({
	className = '',
	initialText = '',
	children,
	beforeChildren,
	conversationId,
	loading = false,
	lastMessageChildren,
	firstMessageChildren,
	canType = true,
	maxHeight = 500,
	onSend = async () => null,
	type
}: Props) {
	const UserContext = useUser();
	const {t} = useTranslation();
	const [api] = useNotification();

	const smsRef = useRef<InputRef>(null);

	const [showSend, setShowSend] = useState<boolean>(false);
	const [firstRender, setFirstRender] = useState<boolean>(true);
	const [isSending, setIsSending] = useState<boolean>(false);
	const [loadedIds, setLoadedIds] = useState<string | null>(null);
	const [loadingSend, setLoadingSend] = useState<boolean>(true); // TODO:
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isManualFetching, setIsManualFetching] = useState<boolean>(false);
	const intervalRef = React.useRef<any>(null);
	const [forceReload, setForceReload] = useState<number>(0);

	useEffect(() => {
		if (isManualFetching)
		{
			intervalRef.current = setInterval(() => {
				console.log('fetching messages (force)');
				setForceReload(Math.random());
			}, 30000);
		}

		return () => {
			if (intervalRef.current)
			{
				clearInterval(intervalRef.current);
			}
		}
	}, [isManualFetching]);

	const [aspectImageRatio, setAspectImageRatio] = useState<number>(1);

	const filterMessages = (messages: ConversationMessage[], user?: UserData | null) => {
		let lastMessageAuthor: ConversationMessage['user'] = undefined;
		const x = [...messages].reverse();

		const output = x?.map((_message, k) => {
			const color = getColor(toSeed(_message.user?.name || 'StableProxy') || 0);
			const message = {..._message, color: color};

			const isMe = isMeMessage(message, user);
			if (isMe)
			{
				return {...message, user: undefined};
			}

			if (lastMessageAuthor && lastMessageAuthor.name == message.user?.name && lastMessageAuthor.role == message.user?.role)
			{
				return {...message, user: undefined};
			}

			lastMessageAuthor = message.user;
			return message;
		}).reverse();

		return output;
	}

	const messagesListState = useState<PageOutput<ConversationMessage>>(getDefaultPageOutput(15));
	const loadMessages: OnPageChange<ConversationMessage> = async (page, perPage, setList, list) => {
		if (!conversationId)
		{
			setLoadingSend(false);
			return
		}

		if (page == 1)
		{
			setFirstRender(true);
		}

		if (!isManualFetching)
		{
			setLoadingSend(true);
		}

		return UserContext.Fetch({
			url: ModifyURL(`/conversation/read/${type}/${conversationId}`),
			params: {page, perPage}
		}).then(res => {
			const output = MapPageOutput(res?.data || {}, (item: any) => item as ConversationMessage);

			// output.data = [...(list?.data || []), ...(output.data || [])]
			if (output.data && output.data.length > 0 && output.current_page && output.current_page > 1)
			{
				output.data = [...(list?.data || []), ...(output.data || [])]
			}

			setList(output);

			setLoadedIds(conversationId)
		}).catch(err => {
			api.error({
				message: err.message || err
			});
		}).finally(() => setLoadingSend(false));
	}

	const listMessagesRef = useRef<HTMLDivElement>(null);

	const scrollToDown = () => {
		if (listMessagesRef.current)
		{
			listMessagesRef.current.scroll({
				top: listMessagesRef.current.scrollHeight,
				behavior: firstRender ? 'instant' : 'smooth'
			});
		}
	}

	const scrollUp = () => {
		if (listMessagesRef.current)
		{
			listMessagesRef.current.scroll({
				top: 0,
				behavior: firstRender ? 'instant' : 'smooth'
			});
		}
	}

	useEffect(() => {
		// scroll
		if (messagesListState[0].data && messagesListState[0].data.length > 0 && typeof window !== 'undefined')
		{

			setTimeout(() => {
				if (messagesListState[0].current_page && messagesListState[0].current_page > 1)
				{
					scrollUp();
				}
				else
				{
					scrollToDown();
				}

				if (firstRender)
				{
					setFirstRender(false);
				}
			}, 10)
		}
	}, [messagesListState[0]]);

	const send = async () => {
		const message = getRefValue(smsRef);

		if (!message && fileList.length == 0)
		{
			return;
		}

		const data = new FormData();
		if (message)
		{
			data.append('message', message);
		}

		if (fileList.length > 0)
		{
			fileList.forEach(file => {
				data.append('files[]', file as RcFile);
			})
		}

		if (onSend)
		{
			const o = await onSend(data);
			if (o === false)
			{
				return;
			}
		}

		if (!conversationId)
		{
			return
		}

		setIsSending(true);

		UserContext.Fetch({
			url: ModifyURL(`/conversation/send/${type}/${conversationId}`),
			method: 'POST',
			body: data
		}).then((res: { data: ConversationMessage }) => {
			if (res?.data)
			{
				addMessage(res.data);
				setSendMessagesIds([...sendMessagesIds, res?.data.uuid]);

				scrollToDown();

				setRefValue(smsRef, '');
				setFileList([]);
				setShowSend(false);

				setIsManualFetching(true);
			}
		}).catch(err => {
			api.error({
				message: err.message || err
			});
		}).finally(() => {
			setIsSending(false);
		});
	}

	const {pusher, isUserConnected, isConnected, onEvent} = usePusher();

	const [messageAddQueue, setMessageAddQueue] = useState<ConversationMessage[]>([]);
	const [sendMessagesIds, setSendMessagesIds] = useState<string[]>([]);

	DelayedEffectHook(() => {
		if (messageAddQueue.length == 0)
		{
			return;
		}

		const state = messageAddQueue;
		setMessageAddQueue([]);

		new Promise<void>(async (resolve) => {
			for (let message of state)
			{
				if (sendMessagesIds.includes(message.uuid))
				{
					continue;
				}

				await addMessage(message);
			}

			resolve();
		}).then(() => {
			console.log('added messages', state);
		});
	}, [messageAddQueue, sendMessagesIds], 500);

	const timeoutCheck = useRef<ReturnType<typeof setTimeout> | null>(null);
	const activeElements = useRef<ConversationMessage[]>([]);
	const ignoreToReadIds = useRef<string[]>([]);
	useEffect(() => {
		if (!conversationId)
		{
			return;
		}

		if (timeoutCheck.current)
		{
			clearInterval(timeoutCheck.current);
		}

		timeoutCheck.current = setInterval(() => {
			// get all non read not from me
			const nonRead = messagesListState[0].data
				?.filter(message => !message.read_at && !isMeMessage(message, UserContext.user) && !ignoreToReadIds.current.includes(message.uuid)) || [];

			const canSee: ConversationMessage[] = [];
			for (let message of nonRead)
			{
				const element = document.getElementById(`message-${message.uuid}`);

				if (element && element.getBoundingClientRect().top < window.innerHeight)
				{
					canSee.push(message);
				}
			}

			activeElements.current = canSee;
		}, 990);

		return () => {
			if (timeoutCheck.current)
			{
				clearInterval(timeoutCheck.current);
			}
		}
	}, [messagesListState]);

	const processReadAt = useRef<boolean>(false);
	useEffect(() => {
		const event = async () => {
			if (processReadAt.current)
			{
				return;
			}

			processReadAt.current = true;

			try
			{
				for (const message of activeElements.current)
				{
					await UserContext.Fetch({
						url: ModifyURL(`/conversation/read/${type}/${conversationId}/${message.uuid}`),
						method: 'PUT',
					}).then((res: { data: { status: string } }) => {
						if (res && res.data && res.data.status == 'success')
						{
							ignoreToReadIds.current.push(message.uuid);
						}
					});
				}
			}
			catch (e)
			{
				console.error(e);
			}
			finally
			{
				processReadAt.current = false;
			}
		};

		let lastProcess = 0;
		const onMouseMove = async () => {
			if (Date.now() - lastProcess > 400)
			{
				lastProcess = Date.now();
				await event();
			}
		}

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('touchstart', onMouseMove);

		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('touchstart', onMouseMove);
		}
	}, []);

	const addMessage = (message: ConversationMessage) => {
		return new Promise<void>(async (resolve, reject) => {
			if (messagesListState[0].data?.find(m => m.id == message.id || m.uuid == message.uuid))
			{
				messagesListState[1]((value) => {
					resolve();
					return {
						...value,
						data: value.data?.map(m => {
							if (m.id == message.id || m.uuid == message.uuid)
							{
								return message;
							}

							return m;
						})
					};
				});
			}
			else
			{
				messagesListState[1]((value) => { // hook set
					resolve();
					return {...messagesListState[0], data: [message, ...(value.data || [])]};
				});
			}

			scrollToDown();
		});
	}

	useEffect(() => {
		if (!loadedIds || !isConnected() || !canType)
		{
			return;
		}

		try
		{
			setRefValue(smsRef, '');
		}
		catch (e)
		{
			console.error(e);
		}

		return onEvent(`private-conversation-${conversationId}-${type.toUpperCase()}`, `conversation-message`, (data: {
			come_from: PublicUser,
			message: ConversationMessage
		}) => {
			setMessageAddQueue((list) => {
				return [...list, data.message];
			});
		});
	}, [pusher, UserContext.user, loadedIds, canType]);

	useEffect(() => {
		setShowSend(initialText.length > 0 || fileList.length > 0 || getRefValue(smsRef)?.length > 0);
	}, [initialText]);

	return (
		<div className={`space-y-4 ${className}`} onPaste={async (e) => {
			// read navigator.clipboard
			const clipboard = navigator.clipboard;
			if (clipboard)
			{
				await clipboard.read().then(async clip => {
					return clip.map(async item => {
						if (item.types.includes('image/png') || item.types.includes('image/jpeg') || item.types.includes('image/jpg'))
						{
							const blob = await item.getType('image/png');
							const uid = Math.random().toString(36).substring(7);
							let ext = blob.type.split('/')[1];
							if (ext == 'jpeg')
							{
								ext = 'jpg';
							}
							const name = `clipboard-${uid}.${ext}`;
							const file: UploadFile = new File([blob], name, {
								type: blob.type,
								lastModified: Date.now(),
							} as any) as any;
							file.uid = uid;
							file.status = 'done';
							file.url = URL.createObjectURL(blob);
							file.thumbUrl = URL.createObjectURL(blob);

							setFileList([...fileList, file]);
							setShowSend(true);
						}
					});
				})
			}

			return false;
		}}>
			{beforeChildren}
			<div
				className={`overflow-y-scroll grow-1`}
				ref={listMessagesRef}
				style={{
					// maxHeight
					flex: '1 1'
				}}
			>
				{conversationId && <PagedItems<ConversationMessage>
					dependenciesResetPages={[conversationId, type]}
					dependencies={[UserContext.user, conversationId, type, forceReload]}
					onChange={loadMessages}
					state={messagesListState}
					className={`mt-2 flex-col-reverse`}
					bodyClassName={`flex flex-col-reverse gap-4 max-w-[100vw] pb-4`}
					format={(items) => filterMessages(items, UserContext.user)}
					loadMore={true}
					loadingTwo={loading}
					switchLoading={!isManualFetching}
					preChildren={lastMessageChildren}
					renderItem={(message, k) => {
						return <React.Fragment key={k}>
							<Message
								message={message}
								isMe={isMeMessage(message, UserContext.user)}
							/>
							{message.user && <MessageAuthor message={message}/>}
						</React.Fragment>
					}}
				/>}

				{children}
			</div>
			{canType && <div>
				<Loading show={isSending || loading} className={`mt-auto sp-conversation-input`} innerClassName={`row-0 flex gap-4`}>
					{/*<Button*/}
					{/*	className={`!h-fit !px-4 !shadow-lg  duration-150 `}*/}
					{/*	size={`large`}*/}
					{/*>*/}
					{/*	<FileAddOutlined/>*/}
					{/*</Button>*/}

					<ImgCrop
						// rotationSlider
						modalOk={t('base-common:words.confirm')}
						modalCancel={t('base-common:words.cancel')}
						modalTitle={t('base-common:words.crop-image')}
						aspectSlider={true}
						aspect={aspectImageRatio}
						beforeCrop={async (file) => {
							if (!file.type.includes("image"))
							{
								return false;
							}

							setAspectImageRatio(1);

							// get image size
							return new Promise((resolve, reject) => {
								const reader = new FileReader();
								reader.onload = (e) => {
									const img = new Image();
									img.onload = () => {
										setAspectImageRatio(img.width / img.height);
										resolve(true);
									}
									img.onerror = () => {
										reject(false);
									}
									img.src = e.target?.result as string;
								}
								reader.readAsDataURL(file);
							});
						}}
					>
						<Upload
							rootClassName={`flex my-auto items-end [&_.ant-upload-list]:flex [&_.ant-upload-list]:gap-2 [&_.ant-upload-list-item-name]:!hidden [&_.ant-upload-list-item-thumbnail]:!flex
							                [&_.ant-upload-list-item-thumbnail]:!justify-center [&_.ant-upload-list-item-thumbnail_span]:!text-xl [&_.ant-upload-list]:before:content-none [&_.ant-upload-list>*]:!m-0 !w-auto ${ConversationStyles.upload}`}
							listType="picture-card"
							accept={`image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt`}
							maxCount={3}
							multiple
							beforeUpload={(file) => {
								if (!file.type.includes("image") && !file.type.includes("audio") && !file.type.includes("video") && !file.type.includes("application/pdf") &&
									!file.type.includes("application/msword") && !file.type.includes("application/vnd.ms-excel") && !file.type.includes("application/vnd.ms-powerpoint") &&
									!file.type.includes("text/plain"))
								{
									api.error({
										message: t('base-common:errors.file-type-not-allowed', {type: file.type})
									});
									return false;
								}

								setFileList([...fileList, file]);
								setShowSend(true);

								return false;
							}}
							onRemove={(file) => {
								const index = fileList.indexOf(file);
								const newFileList = fileList.slice();
								newFileList.splice(index, 1);
								setFileList(newFileList);

								setShowSend(getRefValue(smsRef)?.length > 0 || newFileList.length > 0);
							}}
							fileList={fileList}
							// customRequest={onFileUpload}
						>
							<UploadOutlined/>
						</Upload>
					</ImgCrop>
					<Input.TextArea
						placeholder={t('base-common:input.placeholder.message')}
						className={`w-full sp-input-clear max-h-[100px] content-center`}
						size={`large`}
						rows={1}
						ref={smsRef}
						defaultValue={initialText}
						// count={{
						// 	show: (count) => !showSend && <span className={`text-sm`}>Add more words...</span>
						// }}
						onPressEnter={e => {
							if (e.shiftKey)
							{
								return;
							}

							e.preventDefault();
							e.stopPropagation();

							if (showSend)
							{
								send();
							}
						}}
						onChange={e => {
							setShowSend(e.target.value.length > 0 || fileList.length > 0);
						}}
					/>
					<Button
						className={`!h-fit my-auto !px-4 !shadow-lg !text-white duration-150 ${!showSend && `!grayscale !brightness-125 !opacity-20 pointer-events-none`}`}
						size={`large`}
						onClick={send}
					>
						<SendOutlined className={`!text-black dark:!text-white`}/>
					</Button>
				</Loading>
			</div>}
		</div>
	)
}